import { supabase } from '@/lib/supabase';
import { PackageWithRelations } from '@/features/packages/types';

export const packageQueries = {
  getPackages: async (
    providerId: number,
    versionId: number,
    regionId: number
  ): Promise<PackageWithRelations[]> => {
    const { data, error } = await (supabase
      .from('packages' as any)
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('region_id', regionId)
      .order('display_order', { ascending: true })
      .order('name', { ascending: true }) as any);

    if (error) throw error;

    // Enhance each package with relationships and channel count
    const packages = data || [];
    const enhanced = await Promise.all(
      packages.map(async (pkg: any) => {
        try {
          const [plansResult, categoriesResult, channelsResult] = await Promise.all([
            (supabase.rpc('get_package_plans' as any, { p_package_id: pkg.id }) as any),
            (supabase.rpc('get_package_categories' as any, { p_package_id: pkg.id }) as any),
            (supabase.rpc('get_package_channels' as any, { p_package_id: pkg.id, p_version_id: versionId }) as any)
          ]);

          const plans = (plansResult?.data as any) || [];
          const categories = (categoriesResult?.data as any) || [];
          const channels = (channelsResult?.data as any) || [];

          return {
            ...pkg,
            plans: Array.isArray(plans) ? plans : [],
            categories: Array.isArray(categories) ? categories : [],
            channels: Array.isArray(channels) ? channels : [],
            channel_count: Array.isArray(channels) ? channels.length : 0,
            plan_count: Array.isArray(plans) ? plans.length : 0,
            category_count: Array.isArray(categories) ? categories.length : 0
          } as PackageWithRelations;
        } catch (err) {
          console.error('Error enhancing package:', err);
          // Return minimal package without relationships on error
          return {
            ...pkg,
            plans: [],
            categories: [],
            channels: [],
            channel_count: 0,
            plan_count: 0,
            category_count: 0
          } as PackageWithRelations;
        }
      })
    );

    return enhanced;
  },

  getPackageById: async (id: number, versionId?: number): Promise<PackageWithRelations> => {
    const { data, error } = await (supabase
      .from('packages' as any)
      .select('*')
      .eq('id', id)
      .single() as any);

    if (error) throw error;

    try {
      // Get related plans, categories, and channels
      const [plansResult, categoriesResult, channelsResult] = await Promise.all([
        (supabase.rpc('get_package_plans' as any, { p_package_id: id }) as any),
        (supabase.rpc('get_package_categories' as any, { p_package_id: id }) as any),
        (supabase.rpc('get_package_channels' as any, {
          p_package_id: id,
          p_version_id: versionId || (data as any).version_id
        }) as any)
      ]);

      const plans = (plansResult?.data as any) || [];
      const categories = (categoriesResult?.data as any) || [];
      const channels = (channelsResult?.data as any) || [];

      return {
        ...data,
        plans: Array.isArray(plans) ? plans : [],
        categories: Array.isArray(categories) ? categories : [],
        channels: Array.isArray(channels) ? channels : [],
        channel_count: Array.isArray(channels) ? channels.length : 0,
        plan_count: Array.isArray(plans) ? plans.length : 0,
        category_count: Array.isArray(categories) ? categories.length : 0
      } as PackageWithRelations;
    } catch (err) {
      console.error('Error fetching package relationships:', err);
      // Return minimal package without relationships on error
      return {
        ...data,
        plans: [],
        categories: [],
        channels: [],
        channel_count: 0,
        plan_count: 0,
        category_count: 0
      } as PackageWithRelations;
    }
  },

  getPackagesByRegion: async (
    providerId: number,
    versionId: number,
    regionId: number
  ): Promise<PackageWithRelations[]> => {
    return packageQueries.getPackages(providerId, versionId, regionId);
  }
};
